import { ReactNode } from "react";
import styled, { CSSProperties } from "styled-components";

import Button from "@vfuk/core-button/";
import Icon from "@vfuk/core-icon";
import Spacing from "@vfuk/core-spacing";

import "../../assets/styles/common.scss";
import classes from "./ConfirmModalContent.module.scss";

export enum ButtonType {
  cancel = "cancel",
  confirm = "confirm",
}

export type ConfirmModalContentProps = {
  heading?: string;
  message: string | ReactNode;
  body: string | ReactNode;
  confirmMessage: string;
  onConfirm: () => void;
  cancelMessage?: string;
  onCancel?: () => void;
  loading?: boolean;
  discardButtonAlign?: boolean;
  changedStatus?: string;
  previousStatus?: string;
  disableOkButton?: boolean;
  primaryButton?: ButtonType;
};

const getColor = (state: string) => {
  switch (state.toLowerCase()) {
    case "submitted":
      return "rgb(235, 151, 0)";
    case "accepted":
      return "rgb(0, 94, 165)";
    case "in progress":
      return "rgb(0, 94, 165)";
    case "cancelled":
      return "rgb(189, 0, 0)";
    case "completed":
      return "rgb(0, 138, 0)";
    case "partially fulfilled":
      return "rgb(156,41,160)";
    default:
      return "rgb(190, 190, 190)";
  }
};

const getChangedStatusText = (status: string) => {
  const text =
    status === "Accepted" || status === "In progress" ? "In Progress" : status;
  return text;
};

const StyledSpan = styled.span<{ color: CSSProperties["color"] }>`
  color: ${({ color }) => color};
`;

export const ConfirmModalContent = ({
  heading,
  message,
  body,
  confirmMessage,
  onConfirm,
  cancelMessage,
  onCancel,
  loading,
  discardButtonAlign,
  changedStatus,
  previousStatus,
  disableOkButton,
  primaryButton = ButtonType.confirm,
}: ConfirmModalContentProps): JSX.Element => {
  return (
    <>
      <div className={classes.ConfirmModalContents} role="dialog">
        {heading && <div className="modalHeading">{heading}</div>}
        {previousStatus?.toLowerCase() === "partially fulfilled" &&
          changedStatus?.toLowerCase() === "completed" && (
            <Spacing spacingLevel={{ top: 2, bottom: 2 }}>
              <div>
                <Icon name="error" group="state" size={5} />
              </div>
            </Spacing>
          )}
        {previousStatus && changedStatus && (
          <Spacing spacingLevel={{ top: 4, left: 7, right: 7 }}>
            <div className="modalMessage">
              {message}
              <StyledSpan color={getColor(previousStatus)}>
                {getChangedStatusText(previousStatus)}{" "}
              </StyledSpan>{" "}
              to{" "}
              <StyledSpan color={getColor(changedStatus)}>
                {getChangedStatusText(changedStatus)}
              </StyledSpan>
            </div>
          </Spacing>
        )}
        {!previousStatus && !changedStatus && (
          <Spacing spacingLevel={{ top: 4, left: 7, right: 7 }}>
            <div className="modalMessage">{message}</div>
          </Spacing>
        )}
        <Spacing spacingLevel={{ top: 3, bottom: 5 }}>
          <div className="modalSubmessage">{body}</div>
        </Spacing>
        <br />
        <div
          className={
            discardButtonAlign
              ? classes.Buttons + " " + classes.ButtonReverse
              : classes.Buttons
          }
        >
          {cancelMessage && (
            <div className={(classes.Button, classes.cancelButton)}>
              <Button
                width="auto"
                appearance={
                  primaryButton === ButtonType.cancel ? "primary" : "alt1"
                }
                text={cancelMessage}
                onClick={onCancel}
              />
            </div>
          )}
          <div className={(classes.Button, classes.confirmButton)}>
            <Button
              width="auto"
              appearance={
                primaryButton === ButtonType.confirm ? "primary" : "alt1"
              }
              text={confirmMessage}
              onClick={onConfirm}
              loading={loading}
              state={disableOkButton ? "disabled" : undefined}
            />
          </div>
        </div>
      </div>
    </>
  );
};
